import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { Footer, NavigationBar } from '@rsmus/rsm-catt-ui';
import { UniversalSnackbar } from 'cui/components/global/UniversalSnackbar';
import { SnackbarState } from 'cui/components/global/universalSnackbar.types';
import { footerRedirect } from 'cui/utils/footerConstants';
import rsmLogo from 'cui/assets/img/rsm-logo.png';
import { useAppDispatch, useAppSelector } from 'store/app/hooks';
import { RootState } from 'store/app/store';
import SharedLayoutContext from './SharedLayout.context';
import { SharedLayoutProps } from './SharedLayout.types';
import  homeNavigationMenuData from './navData';
import { useInsightApi } from 'hooks/use-insight-api';
import { sortByApplicationName } from 'utils/common';
import { refreshApplications, setFeatureTypes, setRegisteredApplications } from 'store/app/applications.slice';
import { SharedLayoutHeaderBanner } from './HeaderBanner/SharedLayoutHeaderBanner';

const defaultSnackbarState: SnackbarState = {
  message: "",
  severity: "error",
  visible: false
}

export const snackbarContext = React.createContext(defaultSnackbarState as any);

export const SharedLayout: React.FunctionComponent<SharedLayoutProps> = (props) => {
  const location = useLocation();
  const userAuthorizationValue = useAppSelector((state: RootState) => state.users.userRoleFeatures);
  const dispatch = useAppDispatch()
  const [isNavActive, setIsNavActive] = useState(false);
  const [isScrolled] = useState(false);
  const [navigationMenuButtons, setNavigationMenuButtons] = useState<any[]>([]);
  const [userRole, setUserRole] = useState<any>({});
  const toggleSnackbar = useCallback((s:SnackbarState) => setSnackbarState({ current: s, toggleSnackbar }), []);
  const [snackbarState, setSnackbarState] = useState({ current: defaultSnackbarState, toggleSnackbar });

  const { fetchRegisteredApplicationsList, getFeatureTypes } = useInsightApi();

  const navigationMenuButtonsValue = useMemo(
    () => ({
      navigationMenuButtons,
      setNavigationMenuButtons,
      setUserRole,
      userRole
    }),
    [navigationMenuButtons, userRole]
  );
  const refreshApplicationFlag = useAppSelector(
      (state: RootState) => state.applications.refreshApplications
  )

  useEffect(()=> {
    const fetchFeatureTypes  = async() => {
      const response = await getFeatureTypes();
      if (response) {
        dispatch(setFeatureTypes(response));
      }
    }
    fetchFeatureTypes().catch((error) => {
      console.error(error);
    })
  }, [])

  useEffect(() => {
    if (userAuthorizationValue.length > 0) {
      (async () => {
        try {
          setNavigationMenuButtons(homeNavigationMenuData(userAuthorizationValue));
          setUserRole(userAuthorizationValue);
        } catch (e) {
          console.error(e);
        }
      })();
    }
  }, [userAuthorizationValue]);

  useEffect(() => {
  const fetchApplicationData = async () => {
    const response: any = await fetchRegisteredApplicationsList();
      if (response) {
        const responseSortByName = sortByApplicationName(response);
        dispatch(setRegisteredApplications(responseSortByName));
      }
    }
    if(refreshApplicationFlag) {
      fetchApplicationData()
          .then(() => {
       dispatch(refreshApplications(false))
      });
    }
  }, [refreshApplicationFlag]);

  const tradeRightsYear: string = new Date().getFullYear().toString();
  const themeModeCssURL = useSelector((state: any) => state.themeOptions.currentTheme.themeModeCssURL);
  const appTheme = useSelector((state: any) => state.themeOptions.currentTheme.mode);

  const isReportFullScreen = location.pathname.includes('/reports/fullscreen');

  useLayoutEffect(() => {
    if (!isReportFullScreen) {
      return;
    }

    const updateHeights = () => {
      const main = document.querySelector(
        'main.cui-page.report-fullscreen-main'
      ) as HTMLElement | null;

      if (!main) {
        return;
      }

      const top = main.getBoundingClientRect().top;
      const available = Math.max(0, window.innerHeight - top);
      main.style.height = `${available}px`;
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [isReportFullScreen]);

  const onSnackbarClose = () => {
    setSnackbarState({ ...snackbarState, current: { ...snackbarState.current, visible: false } });
  };

  return (
    <SharedLayoutContext.Provider value={navigationMenuButtonsValue}>
      <snackbarContext.Provider value={{ snackbarState }}>
        <link rel="stylesheet" href={themeModeCssURL} data-theme={appTheme} />
        <div
          id="cui-layout"
          cui-layout-dx={props.maxWidth}
          className={`${props.isNavigationBarEnabled &&
            (isNavActive ? "layout-expanded" : "layout-collapsed")
            } ${isScrolled && "layout-scrolled"}`}
        >
          {props.isNavigationBarEnabled && (
            <NavigationBar
              menu={navigationMenuButtons}
              isNavigationBarEnabled={props.isNavigationBarEnabled}
              isNavActive={isNavActive}
              setIsNavActive={setIsNavActive}
              enableSearch
              enableAccordion
            />
          )}
          <div id="cui-layout_main">
            <main className={`cui-page ${isReportFullScreen ? 'report-fullscreen-main' : ''}`}>
              <div className={`cui-page_wrapper ${isReportFullScreen ? 'report-fullscreen-shell' : ''}`}>
                  <SharedLayoutHeaderBanner/>
                <div className={`cui-page-wrapper-content cui-scroll content-height ${isReportFullScreen ? 'report-fullscreen-wrapper' : ''}`}>
                  <Outlet />
                </div>
                {!isReportFullScreen && (
                  <Footer
                    imgUrl={rsmLogo}
                    footerRedirectPages={footerRedirect}
                    tradeRightsYear={tradeRightsYear}
                  />
                )}
              </div>
            </main>
          </div>
        </div>
        <UniversalSnackbar
          visible={snackbarState.current.visible}
          onSnackbarClose={onSnackbarClose}
          severity={snackbarState.current.severity}
          message={snackbarState.current.message} />
      </snackbarContext.Provider>
    </SharedLayoutContext.Provider>
  );
};
export default SharedLayout;
